import React, { useEffect, useState, useRef } from 'react';
import { Row, Button, Tabs, Layout, Col, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import useProjectContext from '../../../contexts/ProjectContext';
import CreateItemContainer from './utilsProject/CreateItemContainer';
import CreateUpdateForm from './utilsProject/CreateUpdateForm';
import ShowIndicators from '../../home/ShowIndicators';
import ManageFederation from './utilsProject/ManageFederation';
import PlanComForm from './utilsProject/ManagePlanCom/PlanComForm';
import ManagePlanning from './utilsProject/ManagePlanning/ManagePlanning';
import MascotteRouge from '../../../assets/images/PersoRouge.png';
import MascotteOrange from '../../../assets/images/PersoOrange.png';
import MascotteVert from '../../../assets/images/PersoVert.png';
import ShowModalFilesByItem from './utilsProject/ShowModalFilesByItem';
import useHandleResize from '../../../utils/HandleResize';
import ModalConfirm from './utilsProject/ModalConfirm';
import ProjectHeader from '../ProjectHeader';
import {
  getItemVersion,
  isLastItemVersion
} from './utilsProject/ManageVersion/utils';

const { Content } = Layout;
const { TabPane } = Tabs;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

/**
 * Component for displaying project details.
 *
 * @component
 * @param {Object} location - The location object containing information about the current URL.
 * @returns {JSX.Element} ShowProject component
 */
const ShowProject = ({ location }) => {
  const { id } = useParams();
  const {
    getProject,
    project,
    isLoading,
    itemKeysInit,
    checkRole,
    visibleRole,
    handleMascotteClick,
    title,
    setTitle,
    matriceFiles,
    purpose,
    setPurpose,
    visible,
    setVisible,
    tempValue,
    createResource,
    updateResource,
    t
  } = useProjectContext();

  const [modalConfig, setModalConfig] = useState();
  const [index, setIndex] = useState();
  const [fieldsName, setFieldName] = useState();
  const [config, setConfig] = useState();
  const checkManagePlanning = ['planning', 'utility'];
  const { width } = useHandleResize();
  const modalRef = useRef();
  const checkField = ['event', 'note'];
  const [selectedItemVersion, setSelectedItemVersion] = useState();

  const mascotteArray = [MascotteRouge, MascotteOrange, MascotteVert];

  const handleCancel = async () => {
    setPurpose(null);
    await getProject(id);
    setVisible(false);
  };

  const openModal = (
    dataT,
    titleModal,
    indexItem,
    action,
    fields,
    configArg,
    version
  ) => {
    setModalConfig(dataT);
    setTitle(titleModal);
    setIndex(indexItem);
    setPurpose(action);
    setConfig(configArg);
    setFieldName(fields);
    setSelectedItemVersion(version);
    setVisible(true);
  };

  const handleSubmit = (
    values,
    data,
    titleItem,
    field,
    funcCancel,
    version
  ) => {
    if (purpose === 'event') {
      updateResource(values, null, titleItem, field, funcCancel);
    } else if (purpose === 'createFederation') {
      const federationList = { federation: data.federation };
      federationList.federation.push(values.federation);
      createResource(federationList);
    } else if (
      selectedItemVersion?.index < 2 ||
      !isLastItemVersion(project[titleItem], selectedItemVersion)
    ) {
      if (purpose === 'create') createResource(values, titleItem, null);
      else if (field === 'event' && purpose === 'event')
        createResource(values, null, null);
      else
        updateResource(
          values,
          version,
          titleItem,
          checkField.includes(field) ? null : field,
          funcCancel
        );
    } else if (purpose === 'create') {
      const fieldValue = Object.keys(tempValue)[0];
      if (data && Array.isArray(data[fieldValue])) {
        const { event, ...fieldsRest } = data;
        const result = {
          ...values,
          ...fieldsRest,
          [fieldValue]: data[fieldValue].map((e) => {
            if (e._id === tempValue[fieldValue]._id) {
              return { ...tempValue[fieldValue] };
            }
            return { ...e };
          })
        };
        if (tempValue[fieldValue]._id === undefined) {
          result[fieldValue].push({ ...tempValue[fieldValue] });
        }
        createResource(result, titleItem, version?._id, null, funcCancel);
      } else {
        const result = { event: values.event, newItem: tempValue };
        createResource(
          result,
          titleItem,
          selectedItemVersion?._id,
          null,
          funcCancel,
          fieldValue
        );
      }
    } else
      modalRef.current.toggleModal(
        values,
        data,
        titleItem,
        field,
        funcCancel,
        version
      );
  };

  const generateForm = (
    purposeArg,
    version,
    titleArg,
    funcItem,
    dataArg,
    fields,
    configData,
    submitFunction
  ) => {
    if (version) setSelectedItemVersion(version);
    if (titleArg === 'manage_planning' && fields !== 'lifeProject') {
      return (
        <ManagePlanning
          dataItems={dataArg}
          project={project}
          config={configData}
          handleCancel={funcItem}
          handleSubmit={submitFunction || handleSubmit}
          purposeArg={purposeArg}
          version={version}
        />
      );
    }
    if (titleArg === 'plan_com') {
      return (
        <PlanComForm
          dataItems={dataArg}
          title={titleArg}
          projectId={project?._id}
          handleCancel={funcItem}
          handleSubmit={submitFunction || handleSubmit}
          config={configData}
          actorsItem={getItemVersion(project?.actors, -1)?.content}
          purposeArg={purposeArg}
          version={version}
        />
      );
    }
    return (
      <CreateUpdateForm
        purpose={purposeArg}
        resource="projects"
        title={titleArg}
        projectId={project?._id}
        handleCancel={funcItem}
        data={dataArg}
        config={configData}
        fieldsName={fields}
        handleSubmit={submitFunction || handleSubmit}
        version={version}
      />
    );
  };

  useEffect(() => {
    (async () => {
      await getProject(id);
    })();
  }, [getProject]);

  useEffect(() => {
    if (project) checkRole();
    if (location.state && location.item === 'plan_com') {
      const { state } = location;
      openModal(state.purpose, state.title, state.data, null, state.field);
    }
  }, [project]);

  return (
    <>
      {project ? (
        <Spin spinning={isLoading}>
          <ProjectHeader project={project} checkRole={checkRole} />
          <Content>
            <Row>
              <Col
                xxl={20}
                sm={24}
                xs={24}
                style={{
                  margin: width < 1200 ? '0 12px 0 0' : 0,
                  padding: '15px',
                  background: 'var(--clientColor)',
                  borderRadius: '5px',
                  color: 'var(--textColorSecondary)',
                  boxShadow: '0 2px 6px rgb(0 0 0 / 30%)'
                }}
              >
                <ShowIndicators
                  openModal={openModal}
                  project={project}
                  vue="assignee"
                />
              </Col>
              <Col
                xxl={4}
                sm={0}
                xs={0}
                style={{
                  display: width < 1600 ? 'none' : 'flex',
                  justifySelf: 'end'
                }}
              >
                <Button
                  onClick={() => handleMascotteClick(project._id)}
                  style={{
                    background: '#D3D3D3',
                    borderRadius: 5,
                    height: '100%',
                    width: '100%',
                    marginLeft: 16,
                    boxShadow: '0 2px 3px rgb(0 0 0 / 30%)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <img
                    alt="Mascotte de CitizenCan"
                    src={mascotteArray[project?.mascot_state]}
                    height="150px"
                  />
                </Button>
              </Col>
            </Row>
          </Content>
          <Tabs
            defaultActiveKey={location?.item || undefined}
            size="large"
            style={{
              margin: '16px 0'
            }}
            className="show-project-tabs"
          >
            {Object.entries(itemKeysInit).map(([key, value]) => {
              return (
                <TabPane
                  tab={
                    <>
                      <Row
                        justify="center"
                        style={{ marginBottom: 7, textAlign: 'center' }}
                      >
                        {value}
                      </Row>
                      <Row
                        justify="center"
                        style={{ fontSize: 12, textAlign: 'center' }}
                      >
                        {t(`projects.form.items.${key}.title`)}
                      </Row>
                    </>
                  }
                  key={key}
                  span={2}
                >
                  <CreateItemContainer
                    key={key}
                    data={
                      checkManagePlanning.includes(key)
                        ? project.manage_planning
                        : project[key]
                    }
                    title={key}
                    projectId={project._id}
                    getProject={() => getProject(id)}
                    openModal={openModal}
                    visibleRole={visibleRole}
                    generateForm={generateForm}
                    managePlanning={
                      checkManagePlanning.includes(key)
                        ? 'manage_planning'
                        : null
                    }
                    loading={isLoading}
                    actors={
                      project.actors[project.actors.length - 1]?.actors_list
                    }
                  />
                </TabPane>
              );
            })}
          </Tabs>
          {title && purpose ? (
            <Modal
              title={t(`projects.form.items.${title}.title`)}
              visible={visible}
              footer={false}
              destroyOnClose
              onCancel={handleCancel}
              width={1200}
            >
              {title === 'membership' ? (
                <ManageFederation
                  dataItems={modalConfig}
                  title={title}
                  projectId={project?._id}
                  handleCancel={handleCancel}
                  config={config}
                />
              ) : (
                <CreateUpdateForm
                  purpose={purpose}
                  resource="projects"
                  title={title}
                  handleCancel={handleCancel}
                  projectId={project?._id}
                  data={modalConfig}
                  config={config || null}
                  index={index}
                  fieldsName={fieldsName}
                  handleSubmit={handleSubmit}
                  isLoading={isLoading}
                />
              )}
            </Modal>
          ) : null}
          <ShowModalFilesByItem matriceData={matriceFiles} />
          <ModalConfirm destroyOnClose openModal={openModal} ref={modalRef} />
        </Spin>
      ) : (
        <Spin indicator={antIcon} />
      )}
    </>
  );
};
ShowProject.propTypes = {
  location: PropTypes.oneOfType([PropTypes.object])
};

ShowProject.defaultProps = {
  location: null
};

export default ShowProject;
